import React, { useEffect, useState } from "react";
import {
  CreditCard, Gift, Euro, UserPlus, AlertCircle, Mail, Bell, Clock, Activity, TriangleAlert,
  Users, RefreshCw, Database, Send, Webhook, ChevronRight, CheckCircle2, XCircle, HandCoins,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Kpi, Delta, SectionCard, LinkAction, EmptyState, Spinner, Th, PlanBadge, StatusPill, OriginPill,
  StateDot, formatEUR, formatDate, relTime, Avatar,
} from "./adminUi";

const ACTIVITY_ICON = {
  payment_paid: [CheckCircle2, "text-emerald-700 bg-emerald-50"],
  renewal_paid: [RefreshCw, "text-emerald-700 bg-emerald-50"],
  payment_failed: [XCircle, "text-rose-700 bg-rose-50"],
  manual_grant: [Gift, "text-neutral-700 bg-neutral-100"],
  email_retry: [Mail, "text-amber-800 bg-amber-50"],
  webhook_failed: [Webhook, "text-rose-700 bg-rose-50"],
  admin_action: [Activity, "text-neutral-700 bg-neutral-100"],
};

function CheckItem({ icon: Icon, title, sub, count, tone, onClick, testid }) {
  const toneCls = tone === "fail" ? "bg-rose-50 text-rose-700" : tone === "warn" ? "bg-amber-50 text-amber-800" : "bg-neutral-100 text-neutral-700";
  return (
    <button type="button" onClick={onClick} className="w-full flex items-center gap-3 px-4 py-3 rounded-md border border-border hover:bg-neutral-50 transition-colors text-left" data-testid={testid}>
      <span className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${toneCls}`}><Icon className="w-4 h-4" strokeWidth={1.8} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{title}</span>
        <span className="block text-xs text-neutral-500 truncate">{sub}</span>
      </span>
      <span className={`min-w-[28px] h-7 px-2 rounded-full text-xs font-medium inline-flex items-center justify-center tabular-nums ${count > 0 ? toneCls : "bg-neutral-100 text-neutral-500"}`}>{count}</span>
      <ChevronRight className="w-4 h-4 text-neutral-400" />
    </button>
  );
}

function HealthRow({ icon: Icon, name, sub, state, label }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-border last:border-b-0">
      <Icon className="w-4 h-4 text-neutral-500 shrink-0" strokeWidth={1.6} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{name}</div>
        <div className="text-xs text-neutral-500 truncate">{sub}</div>
      </div>
      <StateDot state={state} label={label} />
    </div>
  );
}

export default function AdminDashboard({ period, goTo, onOpenUser, refreshKey }) {
  const [stats, setStats] = useState(null);
  const [cc, setCc] = useState(null);
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/admin/stats"),
      api.get(`/admin/control-center?days=${period}`),
      api.get("/admin/users"),
    ]).then(([s, c, u]) => {
      if (!alive) return;
      setStats(s.data); setCc(c.data);
      const rows = (u.data?.users || []).filter((x) => ["basic","pro","ai_manager"].includes(x.plan));
      setLatest(rows.slice(0, 6));
    }).catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period, refreshKey]);

  if (loading && !cc) return <Spinner />;
  const k = cc?.kpis || {};
  const sys = cc?.system || {};
  const periodLabel = `ultimi ${period} giorni`;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Kpi icon={CreditCard} label="Paganti Stripe" value={stats?.users?.paying ?? "—"} sub="solo pagamenti reali" testid="kpi-users-paying" onClick={() => goTo("subscribers", { origin: "stripe" })} />
        <Kpi icon={Gift} label="Omaggi / Manuali" value={stats?.users?.manual_grants_active ?? 0} sub="grant Admin attivi" tone="warn" testid="kpi-manual-grants" onClick={() => goTo("subscribers", { origin: "manual" })} />
        <Kpi icon={Euro} label="MRR" value={formatEUR(stats?.business?.mrr || 0)} sub="ricavi ricorrenti mensili" tone="primary" testid="kpi-mrr" />
        <Kpi icon={UserPlus} label="Nuovi abbonamenti" value={k.new_subscriptions?.count ?? 0} delta={<Delta current={k.new_subscriptions?.count} previous={k.new_subscriptions?.prev_period} />} sub={periodLabel} tone="ok" testid="kpi-new-subs" />
        <Kpi icon={AlertCircle} label="Pagamenti falliti" value={k.failed_payments?.count ?? 0} delta={<Delta current={k.failed_payments?.period} previous={k.failed_payments?.prev_period} invert />} sub="da recuperare" tone="fail" testid="kpi-failed-payments" onClick={() => goTo("payments", { status: "failed" })} />
        <Kpi icon={Mail} label="Email in errore" value={k.email_problems?.count ?? 0} sub="non consegnate" tone={k.email_problems?.count ? "fail" : "neutral"} testid="kpi-email-problems" onClick={() => goTo("problems", { focus: "email" })} />
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SectionCard icon={Bell} title="Da controllare oggi" action={<LinkAction onClick={() => goTo("problems")} testid="dash-see-all-problems">Vedi tutti</LinkAction>} testid="dash-check-today">
          <div className="space-y-2">
            <CheckItem icon={AlertCircle} tone="fail" title="Pagamenti falliti" sub="Pagamenti Stripe non riusciti da recuperare" count={k.failed_payments?.count ?? 0} onClick={() => goTo("payments", { status: "failed" })} testid="check-failed-payments" />
            <CheckItem icon={TriangleAlert} tone="fail" title="Webhook Stripe falliti" sub="Eventi non processati correttamente" count={k.failed_webhooks?.count ?? 0} onClick={() => goTo("problems", { focus: "stripe" })} testid="check-failed-webhooks" />
            <CheckItem icon={Mail} tone="fail" title="Email di fatturazione non consegnate" sub="Email a clienti/amministrazione non recapitate" count={k.email_problems?.count ?? 0} onClick={() => goTo("problems", { focus: "email" })} testid="check-email-problems" />
            <CheckItem icon={UserPlus} tone="neutral" title="Nuovi abbonamenti" sub={`Stripe ${k.new_subscriptions?.stripe ?? 0} · manuali ${k.new_subscriptions?.manual ?? 0} · ${periodLabel}`} count={k.new_subscriptions?.count ?? 0} onClick={() => goTo("subscribers")} testid="check-new-subs" />
            <CheckItem icon={Clock} tone="warn" title="Cancellazioni / past_due da gestire" sub={`Fine piano richiesta ${k.cancellations?.pending ?? 0} · past_due ${k.past_due_users?.count ?? 0}`} count={(k.cancellations?.count ?? 0) + (k.past_due_users?.count ?? 0)} onClick={() => goTo("subscribers", { billing_status: "past_due" })} testid="check-cancellations" />
          </div>
        </SectionCard>

        <SectionCard icon={Clock} title="Attività recenti" action={<LinkAction onClick={() => goTo("problems", { focus: "activity" })} testid="dash-see-all-activity">Vedi tutte</LinkAction>} testid="dash-activity" padded={false}>
          {(cc?.recent_activity || []).length === 0 ? <EmptyState>Nessuna attività registrata.</EmptyState> : (
            <ul className="divide-y divide-border">
              {cc.recent_activity.slice(0, 8).map((a, i) => {
                const [Icon, cls] = ACTIVITY_ICON[a.kind] || ACTIVITY_ICON.admin_action;
                return (
                  <li key={i} className={`px-5 py-3 flex items-start gap-3 ${a.user_id ? "cursor-pointer hover:bg-neutral-50" : ""}`} onClick={() => a.user_id && onOpenUser(a.user_id)}>
                    <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 ${cls}`}><Icon className="w-4 h-4" strokeWidth={1.8} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.title}{a.amount != null ? <span className="text-neutral-500 font-normal"> · {formatEUR(a.amount)}</span> : null}</div>
                      <div className="text-xs text-neutral-500 truncate">{a.subtitle}</div>
                    </div>
                    <span className="text-[11px] text-neutral-400 whitespace-nowrap">{relTime(a.at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard icon={Activity} title="Salute del sistema" testid="dash-health">
          <HealthRow icon={CreditCard} name="Stripe" sub={sys.stripe?.mode ? `Pagamenti e abbonamenti · modalità ${sys.stripe.mode}` : "Pagamenti e abbonamenti"} state={sys.stripe?.state} label={sys.stripe?.state === "fail" ? "Non configurato" : undefined} />
          <HealthRow icon={Webhook} name="Webhook Stripe" sub={sys.webhook?.last_received_at ? `Ultimo evento ${relTime(sys.webhook.last_received_at)} · ${sys.webhook.failed_total} falliti` : "Nessun evento ricevuto"} state={sys.webhook?.state} label={sys.webhook?.state === "fail" ? "Secret mancante" : sys.webhook?.state === "warn" ? `${sys.webhook.failed_total} falliti` : undefined} />
          <HealthRow icon={Send} name="Resend" sub={sys.resend?.from ? `Invio email · ${sys.resend.from}` : "Invio email"} state={sys.resend?.state} label={sys.resend?.state === "fail" ? "Non configurato" : undefined} />
          <HealthRow icon={Database} name="Database" sub="Lettura e scrittura dati" state={sys.database?.state} />
          <HealthRow icon={Mail} name="Billing email" sub={sys.billing_email?.problem_count ? `${sys.billing_email.problem_count} email problematiche` : "Pipeline fatturazione"} state={sys.billing_email?.state} label={sys.billing_email?.state === "warn" ? "Attenzione" : undefined} />
          <div className="pt-3 text-[11px] text-neutral-400">Ultimo controllo: {sys.checked_at ? relTime(sys.checked_at) : "—"}</div>
        </SectionCard>
      </div>

      <SectionCard icon={Users} title="Ultimi abbonati" action={<LinkAction onClick={() => goTo("subscribers")} testid="dash-go-subscribers">Vai alla sezione Abbonati</LinkAction>} padded={false} testid="dash-latest-subscribers">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-neutral-50/60"><Th>Nome</Th><Th>Email</Th><Th>Piano</Th><Th>Stato</Th><Th>Origine</Th><Th>Data</Th><Th align="right">Pagamento</Th></tr></thead>
            <tbody>
              {latest.length === 0 && <tr><td colSpan={7}><EmptyState>Nessun abbonato.</EmptyState></td></tr>}
              {latest.map((u) => (
                <tr key={u.id} className="border-b border-border last:border-b-0 hover:bg-neutral-50/80 cursor-pointer" onClick={() => onOpenUser(u.id)} data-testid={`dash-sub-row-${u.id}`}>
                  <td className="py-3 px-4"><div className="flex items-center gap-2"><Avatar name={u.name} email={u.email} /><span className="font-medium truncate max-w-[200px]">{u.name || "—"}</span></div></td>
                  <td className="py-3 px-4 text-neutral-600 truncate max-w-[240px]">{u.email}</td>
                  <td className="py-3 px-4"><PlanBadge plan={u.plan} /></td>
                  <td className="py-3 px-4"><StatusPill status={u.billing_status} suspended={u.suspended} periodEnd={u.current_period_end} /></td>
                  <td className="py-3 px-4"><OriginPill origin={u.origin} /></td>
                  <td className="py-3 px-4 tabular-nums text-neutral-600">{formatDate(u.created_at)}</td>
                  <td className="py-3 px-4 text-right tabular-nums">
                    {u.origin === "gift" || u.origin === "manual" ? <span className="text-neutral-500 text-xs inline-flex items-center gap-1"><HandCoins className="w-3.5 h-3.5" /> Omaggio</span>
                      : u.latest_payment ? <span className={u.latest_payment.status === "paid" ? "text-neutral-900" : "text-rose-700"}>{formatEUR(u.latest_payment.amount)}</span>
                      : <span className="text-neutral-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
